import React, { useState, Component } from 'react';
import { Search, Plus, Upload, X, Mail, Pencil, Trash2, ChevronDown, Send, CheckCircle2, FileText, Clock, MailOpen, PlayCircle, AlertTriangle } from 'lucide-react';
type Status = 'pending' | 'opened' | 'started' | 'completed' | 'expired';
type Respondent = {
  id: number;
  name: string;
  email: string;
  relationship: string;
  invited: string;
  lastActivity: string;
  status: Status;
};
const STATUS_META: Record<Status, {
  label: string;
  badge: string;
  dot: string;
  icon: ComponentType<{
    className?: string;
  }>;
}> = {
  pending: {
    label: 'Pending',
    badge: 'bg-slate-100 text-slate-700',
    dot: 'bg-slate-400',
    icon: Clock
  },
  opened: {
    label: 'Opened',
    badge: 'bg-blue-100 text-blue-700',
    dot: 'bg-blue-500',
    icon: MailOpen
  },
  started: {
    label: 'Started',
    badge: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-500',
    icon: PlayCircle
  },
  completed: {
    label: 'Completed',
    badge: 'bg-emerald-100 text-emerald-700',
    dot: 'bg-emerald-500',
    icon: CheckCircle2
  },
  expired: {
    label: 'Expired',
    badge: 'bg-red-100 text-red-700',
    dot: 'bg-red-500',
    icon: AlertTriangle
  }
};
const STATUS_ORDER: Status[] = ['pending', 'opened', 'started', 'completed', 'expired'];
const INITIAL: Respondent[] = [{
  id: 1,
  name: 'Sarah Connor',
  email: 'sarah.connor@acme.com',
  relationship: 'Manager',
  invited: 'Oct 1',
  lastActivity: 'Oct 3',
  status: 'completed'
}, {
  id: 2,
  name: 'Michael Scott',
  email: 'michael.scott@acme.com',
  relationship: 'Peer',
  invited: 'Oct 1',
  lastActivity: 'Oct 2',
  status: 'started'
}, {
  id: 3,
  name: 'Jim Halpert',
  email: 'jim.halpert@acme.com',
  relationship: 'Peer',
  invited: 'Oct 1',
  lastActivity: 'Oct 1',
  status: 'opened'
}, {
  id: 4,
  name: 'Pam Beesly',
  email: 'pam.beesly@acme.com',
  relationship: 'Direct Report',
  invited: 'Oct 1',
  lastActivity: '—',
  status: 'pending'
}, {
  id: 5,
  name: 'Dwight Schrute',
  email: 'dwight@acme.com',
  relationship: 'Peer',
  invited: 'Oct 1',
  lastActivity: '—',
  status: 'pending'
}, {
  id: 6,
  name: 'External Reviewer',
  email: 'reviewer@partner.com',
  relationship: 'External',
  invited: 'Sep 20',
  lastActivity: 'Sep 20',
  status: 'expired'
}, {
  id: 7,
  name: 'Angela Martin',
  email: 'angela@acme.com',
  relationship: 'Direct Report',
  invited: 'Oct 1',
  lastActivity: 'Oct 4',
  status: 'completed'
}];
export function RespondentManagement() {
  const [respondents, setRespondents] = useState<Respondent[]>(INITIAL);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Respondent | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<Respondent | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };
  const counts = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = respondents.filter(r => r.status === s).length;
    return acc;
  }, {} as Record<Status, number>);
  const filtered = respondents.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (search && !r.name.toLowerCase().includes(search.toLowerCase()) && !r.email.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (r: Respondent) => {
    setEditing(r);
    setDrawerOpen(true);
  };
  const removeRespondent = (id: number) => {
    setRespondents(prev => prev.filter(r => r.id !== id));
    setConfirmRemove(null);
    setSelected(prev => prev.filter(s => s !== id));
    showToast('Respondent removed.');
  };
  const toggleSelect = (id: number) => {
    setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const remindable = filtered.filter(r => r.status === 'pending' || r.status === 'opened' || r.status === 'expired');
  return <div className="animate-in fade-in duration-500" data-id="element-2190" data-mp="1tps4le5e8g3o">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-2191" data-mp="1fxtylc6zb0bq">
        <div data-id="element-2192" data-mp="1jz75zqppzbkr">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-1" data-id="element-2193" data-mp="1jyxhk7kbpch6">
            <span data-id="element-2194" data-mp="15zxpsr1ikhgs">Assessment</span>
            <button className="inline-flex items-center font-medium text-slate-900 hover:text-slate-700" data-id="element-2195" data-mp="160pl00r78d6j">
              Q3 Leadership Review <ChevronDown className="h-4 w-4 ml-1" data-id="element-2196" data-mp="rbnvcyh1cjc0" />
            </button>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-2197" data-mp="mcukzex1p8g2">Respondents</h1>
        </div>
        <div className="flex gap-3 shrink-0" data-id="element-2198" data-mp="11a3pzbf9uaq9">
          <button onClick={() => setImportOpen(true)} className="border border-slate-300 text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors flex items-center" data-id="element-2199" data-mp="11b54xqzerhd9">
            <Upload className="h-4 w-4 mr-2" data-id="element-2200" data-mp="1a8p3ut9st1kp" /> Import CSV
          </button>
          <button onClick={openCreate} className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-2201" data-mp="15b5r5xsr0wuv">
            <Plus className="h-4 w-4 mr-2" data-id="element-2202" data-mp="1f2a5cvalodon" /> Add Respondent
          </button>
        </div>
      </div>

      {/* Status summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-id="element-2203" data-mp="1ozoc236q1o3r">
        {STATUS_ORDER.map(s => {
        const meta = STATUS_META[s];
        const Icon = meta.icon;
        const active = statusFilter === s;
        return <button key={s} onClick={() => setStatusFilter(active ? 'all' : s)} className={`bg-white border rounded-xl p-4 text-left transition-all ${active ? 'border-slate-900 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-2204" data-mp="2130o2dnpo0wu">
              <div className="flex items-center justify-between mb-2" data-id="element-2205" data-mp="2eskld3yiw5yr">
                <span className="text-xs font-medium text-slate-500" data-id="element-2206" data-mp="2jrxichj3ytf2">
                  {meta.label}
                </span>
                <Icon className="h-4 w-4 text-slate-400" data-id="element-2207" data-mp="2osv0wv815ph3" />
              </div>
              <div className="text-2xl font-semibold text-slate-900" data-id="element-2208" data-mp="2tr678vz49huk">
                {counts[s]}
              </div>
            </button>;
      })}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4" data-id="element-2209" data-mp="zc3nzg0nuw03">
        <div className="relative flex-1 max-w-md" data-id="element-2210" data-mp="3cmn1ko4428c5">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-2211" data-mp="3co6fw967i9ba" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search respondents..." className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-2212" data-mp="37vdb5laqn0z7" />
        </div>
        <div className="flex items-center gap-3" data-id="element-2213" data-mp="3rkfd756jecl2">
          {selected.length > 0 && <span className="text-sm text-slate-500" data-id="element-2214" data-mp="3ml2rinrejbk2">
              {selected.length} selected
            </span>}
          <button onClick={() => {
          showToast(`Reminders sent to ${selected.length || remindable.length} respondent(s).`);
          setSelected([]);
        }} disabled={remindable.length === 0} className="bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2215" data-mp="3hmht8tvpn1y9">
            <Send className="h-4 w-4 mr-2" data-id="element-2216" data-mp="6a85ez86c8b" /> Send Reminders
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2217" data-mp="55d3u8tbf63m">
        <table className="w-full text-left border-collapse" data-id="element-2218" data-mp="svh5iamy0vrz">
          <thead data-id="element-2219" data-mp="xszicc42zv59">
            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-medium" data-id="element-2220" data-mp="xtines8mup1v">
              <th className="p-4 w-10" data-id="element-2221" data-mp="j3tjip95o6xw"></th>
              <th className="p-4" data-id="element-2222" data-mp="o3fwcxl2nl66">Respondent</th>
              <th className="p-4" data-id="element-2223" data-mp="o24s6wnpsbej">Relationship</th>
              <th className="p-4" data-id="element-2224" data-mp="1bt0exfdbcgex">Status</th>
              <th className="p-4" data-id="element-2225" data-mp="1bs8m2cb3owsc">Invited</th>
              <th className="p-4" data-id="element-2226" data-mp="1gjqlgvxh6pb7">Last Activity</th>
              <th className="p-4 text-right" data-id="element-2227" data-mp="1gkihu8ywcr2j">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 text-sm" data-id="element-2228" data-mp="3194708rhxekl">
            {filtered.length === 0 ? <tr data-id="element-2229" data-mp="2wa9n13hsljyw">
                <td colSpan={7} className="p-12 text-center text-slate-500" data-id="element-2230" data-mp="2waswdvtdn01b">
                  No respondents match your filters.
                </td>
              </tr> : filtered.map(r => {
            const meta = STATUS_META[r.status];
            const canRemind = r.status === 'pending' || r.status === 'opened' || r.status === 'expired';
            return <tr key={r.id} className="hover:bg-slate-50 transition-colors group" data-id="element-2231" data-mp="2rd1430otlbwl">
                    <td className="p-4" data-id="element-2232" data-mp="2nch4zrkory7t">
                      <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleSelect(r.id)} className="h-4 w-4 text-slate-900 focus:ring-slate-900 border-slate-300 rounded" data-id="element-2233" data-mp="6kzbyntb03x6" />
                    </td>
                    <td className="p-4" data-id="element-2234" data-mp="3k94kv5lwv8qc">
                      <div className="flex items-center" data-id="element-2235" data-mp="3fhctwtyuf0zo">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 mr-3 shrink-0" data-id="element-2236" data-mp="3ffs0j7o9lo3e">
                          {r.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="min-w-0" data-id="element-2237" data-mp="2wi8nlsyorlbp">
                          <div className="font-medium text-slate-900 truncate" data-id="element-2238" data-mp="2wj0i10gjrd0e">
                            {r.name}
                          </div>
                          <div className="text-xs text-slate-500 flex items-center truncate" data-id="element-2239" data-mp="urx687ux7se9">
                            <Mail className="h-3 w-3 mr-1" data-id="element-2240" data-mp="prrbgitctpry" /> {r.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600" data-id="element-2241" data-mp="12awna6uwt7ug">{r.relationship}</td>
                    <td className="p-4" data-id="element-2242" data-mp="128tpv1rfgcfp">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${meta.badge}`} data-id="element-2243" data-mp="1713co8x8wwdr">
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${meta.dot}`} data-id="element-2244" data-mp="ecxqdhqjmzbt"></span>
                        {meta.label}
                      </span>
                    </td>
                    <td className="p-4 text-slate-600" data-id="element-2245" data-mp="oavl8o2wrude">{r.invited}</td>
                    <td className="p-4 text-slate-600" data-id="element-2246" data-mp="scgxc77mc4mj">{r.lastActivity}</td>
                    <td className="p-4" data-id="element-2247" data-mp="samdkw832ik1">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2248" data-mp="1zr24gc2tr7m4">
                        {canRemind && <button onClick={() => showToast(`Reminder sent to ${r.name}.`)} title="Send reminder" className="p-1.5 text-slate-400 hover:text-slate-900 rounded-md hover:bg-slate-100" data-id="element-2249" data-mp="4n42ctmmj5ir">
                            <Send className="h-4 w-4" data-id="element-2250" data-mp="8pil7dqam5a1" />
                          </button>}
                        <button onClick={() => openEdit(r)} title="Edit" className="p-1.5 text-slate-400 hover:text-slate-900 rounded-md hover:bg-slate-100" data-id="element-2251" data-mp="3h26dsgnqekwy">
                          <Pencil className="h-4 w-4" data-id="element-2252" data-mp="3m2u38xwbatp2" />
                        </button>
                        <button onClick={() => setConfirmRemove(r)} title="Remove" className="p-1.5 text-slate-400 hover:text-red-600 rounded-md hover:bg-red-50" data-id="element-2253" data-mp="2yczrykrn5d6x">
                          <Trash2 className="h-4 w-4" data-id="element-2254" data-mp="1dndv3kh5bqog" />
                        </button>
                      </div>
                    </td>
                  </tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Drawer */}
      {drawerOpen && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-2255" data-mp="1sl6b0dqluh36">
          <div className="absolute inset-0 bg-slate-900/30 animate-in fade-in" onClick={() => setDrawerOpen(false)} data-id="element-2256" data-mp="1sisgc5zu76w5" />
          <div className="relative w-full max-w-md bg-white h-full shadow-xl flex flex-col animate-in slide-in-from-right duration-300" data-id="element-2257" data-mp="1inibzblyunf7">
            <div className="flex items-center justify-between px-6 h-16 border-b border-slate-200" data-id="element-2258" data-mp="1njz22bcq7bux">
              <h2 className="text-lg font-semibold text-slate-900" data-id="element-2259" data-mp="768wf05ykfj6">
                {editing ? 'Edit Respondent' : 'Add Respondent'}
              </h2>
              <button onClick={() => setDrawerOpen(false)} className="text-slate-400 hover:text-slate-600" data-id="element-2260" data-mp="rgthhibi4qfn">
                <X className="h-5 w-5" data-id="element-2261" data-mp="mngypoti7dut" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-5" data-id="element-2262" data-mp="ioht01zy7d2b">
              <div data-id="element-2263" data-mp="dpohgf481w4s">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-2264" data-mp="dq6irq5qr0jk">
                  Full Name *
                </label>
                <input type="text" defaultValue={editing?.name || ''} placeholder="e.g., Sarah Connor" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-2265" data-mp="1acte56ffci5k" />
              </div>
              <div data-id="element-2266" data-mp="3m5hw0oezsxj7">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-2267" data-mp="3m603kh73uy6f">
                  Email Address *
                </label>
                <input type="email" defaultValue={editing?.email || ''} placeholder="name@company.com" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-2268" data-mp="371x7d5ii3kpl" />
              </div>
              <div data-id="element-2269" data-mp="3lrw91i99le9t">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-2270" data-mp="3ps6n7i77nazr">
                  Relationship
                </label>
                <select defaultValue={editing?.relationship || 'Peer'} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-2271" data-mp="3uqgg1m5gpzi7">
                  <option data-id="element-2272" data-mp="25cuw3x9ues27">Self</option>
                  <option data-id="element-2273" data-mp="25dw7y3yonikz">Manager</option>
                  <option data-id="element-2274" data-mp="2abemc9vgyq0d">Peer</option>
                  <option data-id="element-2275" data-mp="2acfv27p0j0ex">Direct Report</option>
                  <option data-id="element-2276" data-mp="2ad95bin6k81t">External</option>
                </select>
              </div>
              {editing && <div data-id="element-2277" data-mp="1xbvljz0khr03">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-2278" data-mp="1xcf7rrx931jx">
                    Status
                  </label>
                  <select defaultValue={editing.status} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-2279" data-mp="2ca7kydfbrduy">
                    {STATUS_ORDER.map(s => <option key={s} value={s} data-id="element-2280" data-mp="2g8nb2q6pd2cl">
                        {STATUS_META[s].label}
                      </option>)}
                  </select>
                </div>}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3" data-id="element-2281" data-mp="33zkfzj02yzec">
              <button onClick={() => setDrawerOpen(false)} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-white transition-colors" data-id="element-2282" data-mp="2zz9yb92xekjn">
                Cancel
              </button>
              <button onClick={() => {
            setDrawerOpen(false);
            showToast(editing ? 'Respondent updated.' : 'Respondent added & invited.');
          }} className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-2283" data-mp="39m30tvmwe6jz">
                {editing ? 'Save Changes' : 'Add & Invite'}
              </button>
            </div>
          </div>
        </div>}

      {/* Import CSV Modal */}
      {importOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2284" data-mp="2gx5o081gh1ym">
          <div className="absolute inset-0 bg-slate-900/30 animate-in fade-in" onClick={() => setImportOpen(false)} data-id="element-2285" data-mp="2lx1qg2ui8lc2" />
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl animate-in zoom-in-95 duration-200" data-id="element-2286" data-mp="31dqilwyn5zk8">
            <div className="flex items-center justify-between px-6 h-14 border-b border-slate-200" data-id="element-2287" data-mp="2wdm4uep4bixp">
              <h2 className="text-base font-semibold text-slate-900" data-id="element-2288" data-mp="2sfo9sv8o22me">
                Import Respondents
              </h2>
              <button onClick={() => setImportOpen(false)} className="text-slate-400 hover:text-slate-600" data-id="element-2289" data-mp="2ig796s89uexa">
                <X className="h-5 w-5" data-id="element-2290" data-mp="2dmukyj7i8a52" />
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-2291" data-mp="28qdx7q47vxsi">
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center" data-id="element-2292" data-mp="24o9916wjftd9">
                <Upload className="h-8 w-8 text-slate-400 mx-auto mb-3" data-id="element-2293" data-mp="1zp6920vf0dc5" />
                <p className="text-sm font-medium text-slate-900" data-id="element-2294" data-mp="1zr8yojsno4gc">
                  Drag & drop your CSV file
                </p>
                <p className="text-xs text-slate-500 mt-1" data-id="element-2295" data-mp="a8aqgb2hs7n2">
                  or click to browse
                </p>
                <button className="mt-4 text-sm font-medium text-slate-900 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50" data-id="element-2296" data-mp="f6cbv2ne4j1p">
                  Choose File
                </button>
              </div>
              <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3" data-id="element-2297" data-mp="o6ikaa48esu0">
                <FileText className="h-4 w-4 shrink-0 mt-0.5" data-id="element-2298" data-mp="3nkuc4i73auar" />
                <span data-id="element-2299" data-mp="3sn37fl7fq8g1">
                  CSV columns: <strong data-id="element-2300" data-mp="3sjxjwaegfemf">name, email, relationship</strong>.
                  Invitations are sent automatically on import.{' '}
                  <a href="#" className="text-slate-900 underline" data-id="element-2301" data-mp="58pijb5cgoy7">
                    Download template
                  </a>
                </span>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3" data-id="element-2302" data-mp="38y17c6v756wt">
              <button onClick={() => setImportOpen(false)} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-white transition-colors" data-id="element-2303" data-mp="2gcl5cqieie0r">
                Cancel
              </button>
              <button onClick={() => {
            setImportOpen(false);
            showToast('Respondents imported & invited.');
          }} className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-2304" data-mp="2pb6jm9gub6vq">
                Import & Invite
              </button>
            </div>
          </div>
        </div>}

      {/* Remove Confirm */}
      {confirmRemove && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2305" data-mp="2bet2j1g7x7hs">
          <div className="absolute inset-0 bg-slate-900/30 animate-in fade-in" onClick={() => setConfirmRemove(null)} data-id="element-2306" data-mp="1dxcl36e2gi3r" />
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6 animate-in zoom-in-95 duration-200" data-id="element-2307" data-mp="1bdfwlscvfgen">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mb-4" data-id="element-2308" data-mp="16ebpd79ibsqy">
              <Trash2 className="h-5 w-5 text-red-600" data-id="element-2309" data-mp="11eqm4knb0y8f" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-2310" data-mp="xe6qxmmak4ox">
              Remove respondent?
            </h2>
            <p className="text-sm text-slate-500 mb-6" data-id="element-2311" data-mp="sf3jjviu9mds">
              <strong data-id="element-2312" data-mp="sgn4hzffwllw">{confirmRemove.name}</strong> will be removed from this
              assessment and any pending invitation will be revoked. This cannot
              be undone.
            </p>
            <div className="flex justify-end gap-3" data-id="element-2313" data-mp="imo3j3r3r7vj">
              <button onClick={() => setConfirmRemove(null)} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-2314" data-mp="dqz3z0f31qz8">
                Cancel
              </button>
              <button onClick={() => removeRespondent(confirmRemove.id)} className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors" data-id="element-2315" data-mp="3r6rryoga6oo7">
                Remove
              </button>
            </div>
          </div>
        </div>}

      {/* Toast */}
      {toast && <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-4 py-3 rounded-lg shadow-lg flex items-center text-sm font-medium animate-in slide-in-from-bottom-2 fade-in" data-id="element-2316" data-mp="ds4wecr0le4v">
          <CheckCircle2 className="h-4 w-4 mr-2 text-emerald-400" data-id="element-2317" data-mp="it0xyjnw8o74" />
          {toast}
        </div>}
    </div>;
}